// src/components/Events/SignInModal.jsx
import React, { useState, useRef } from 'react';
import html2canvas from 'html2canvas';
import { supabase } from '../../supabase';
import './SignInModal.css';

const SignInModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({
    name: '',
    nationality: '',
    institutionType: '',
    institutionName: '',
    position: '',
    phone: '',
    email: '',
    institutionContactPerson: '',
    institutionPhone: '',
    institutionEmail: '',
    invitationCode: '',
    photo: null,
    photoPreview: null
  });
  const [idData, setIdData] = useState(null);
  const [codeError, setCodeError] = useState('');
  const [showGuidelines, setShowGuidelines] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const idCardRef = useRef(null);
  const fileInputRef = useRef(null);

  const VALID_CODE = 'abvig-aicis2026/mfa';

  const nationalities = [
    'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Ethiopia', 
    'Tanzania', 'Uganda', 'Algeria', 'Sudan', 'Morocco', 'Angola', 
    'Mozambique', 'Madagascar', 'Cameroon', 'Côte d\'Ivoire', 'Niger', 
    'Burkina Faso', 'Mali', 'Malawi', 'Zambia', 'Senegal', 'Chad', 
    'Somalia', 'Zimbabwe', 'Guinea', 'Rwanda', 'Benin', 'Burundi', 
    'Tunisia', 'South Sudan', 'Togo', 'Sierra Leone', 'Libya', 
    'Congo', 'Liberia', 'Central African Republic', 'Mauritania', 
    'Eritrea', 'Namibia', 'Gambia', 'Botswana', 'Gabon', 'Lesotho', 
    'Guinea-Bissau', 'Equatorial Guinea', 'Mauritius', 'Eswatini', 
    'Djibouti', 'Comoros', 'Cabo Verde', 'São Tomé and Príncipe', 
    'Seychelles', 'Other'
  ];

  const institutionTypes = ['Government', 'Private'];

  const validateForm = () => {
    const errors = {};
    let hasError = false;

    if (!formData.invitationCode.trim()) {
      errors.invitationCode = 'Invitation code is required';
      hasError = true;
    } else if (formData.invitationCode.trim() !== VALID_CODE) {
      errors.invitationCode = 'Invalid invitation code. Please check your invitation letter.';
      hasError = true;
    }

    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
      hasError = true;
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
      hasError = true;
    }

    if (!formData.nationality) {
      errors.nationality = 'Please select your nationality';
      hasError = true;
    }

    if (!formData.institutionType) {
      errors.institutionType = 'Please select institution type';
      hasError = true;
    }

    if (!formData.institutionName.trim()) {
      errors.institutionName = 'Institution name is required';
      hasError = true;
    }

    if (!formData.position.trim()) {
      errors.position = 'Position is required';
      hasError = true;
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      hasError = true;
    } else if (!/^[\d\s\-()+]{8,20}$/.test(formData.phone.trim())) {
      errors.phone = 'Please enter a valid phone number with country code';
      hasError = true;
    }

    if (!formData.email.trim()) {
      errors.email = 'Email address is required';
      hasError = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
      hasError = true;
    }

    if (!formData.institutionContactPerson.trim()) {
      errors.institutionContactPerson = 'Institution contact person is required';
      hasError = true;
    }

    if (!formData.institutionPhone.trim()) {
      errors.institutionPhone = 'Institution phone number is required';
      hasError = true;
    } else if (!/^[\d\s\-()+]{8,20}$/.test(formData.institutionPhone.trim())) {
      errors.institutionPhone = 'Please enter a valid institution phone number';
      hasError = true;
    }

    if (!formData.institutionEmail.trim()) {
      errors.institutionEmail = 'Institution email is required';
      hasError = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.institutionEmail.trim())) {
      errors.institutionEmail = 'Please enter a valid institution email address';
      hasError = true;
    }

    if (!formData.photoPreview) {
      errors.photo = 'Please upload a clear photo';
      hasError = true;
    }

    setValidationErrors(errors);
    return !hasError;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
    if (name === 'invitationCode' && codeError) {
      setCodeError('');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        alert('Please upload a JPEG or PNG image file.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Photo size must be less than 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          photo: file,
          photoPreview: reader.result
        });
        if (validationErrors.photo) {
          setValidationErrors({
            ...validationErrors,
            photo: ''
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const errorMessages = Object.values(validationErrors).filter(msg => msg);
      if (errorMessages.length > 0) {
        alert('⚠️ Please fix the following errors:\n\n' + errorMessages.map((msg, i) => `  ${i + 1}. ${msg}`).join('\n'));
      }
      const firstErrorField = document.querySelector('.input-error');
      if (firstErrorField) {
        firstErrorField.focus();
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (formData.invitationCode.trim() !== VALID_CODE) {
      setCodeError('Invalid invitation code. Please check your invitation letter.');
      alert('⚠️ Invalid invitation code. Please check your invitation letter.');
      return;
    }
    
    const idNumber = 'AICIS-' + Date.now().toString().slice(-6);
    
    let photoUrl = null;
    if (formData.photo) {
      try {
        const fileExt = formData.photo.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('registration-photos')
          .upload(fileName, formData.photo);
        
        if (uploadError) {
          console.error('Upload error:', uploadError);
          alert('❌ There was an error uploading your photo. Please try again.');
          return;
        } else {
          const { data: urlData } = supabase.storage
            .from('registration-photos')
            .getPublicUrl(fileName);
          photoUrl = urlData.publicUrl;
        }
      } catch (err) {
        console.error('Error uploading photo:', err);
        alert('❌ There was an error uploading your photo. Please try again.');
        return;
      }
    }
    
    try {
      const { error } = await supabase
        .from('registrations')
        .insert([
          {
            id_number: idNumber,
            name: formData.name,
            nationality: formData.nationality,
            institution_type: formData.institutionType,
            institution_name: formData.institutionName,
            position: formData.position,
            phone: formData.phone,
            email: formData.email,
            contact_person: formData.institutionContactPerson,
            contact_phone: formData.institutionPhone,
            contact_email: formData.institutionEmail,
            photo_url: photoUrl
          }
        ]);

      if (error) {
        console.error('Database error:', error);
        alert('❌ Database error: ' + error.message);
        return;
      }

      alert('✅ Registration successful! Your ID card is being generated.');

      setIdData({
        ...formData,
        idNumber,
        photoUrl,
        date: new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      });
      setStep('idcard');
      
    } catch (err) {
      console.error('Error:', err);
      alert('❌ Something went wrong. Please try again.');
    }
  };

  // ===== FIXED DOWNLOAD FUNCTION =====
  const handleDownload = async () => {
    if (!idCardRef.current) {
      alert('❌ ID Card not found. Please try again.');
      return;
    }

    setIsDownloading(true);

    try {
      const cardElement = idCardRef.current;
      
      // Create a clone of the card to avoid modifying the original
      const clone = cardElement.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.top = '0';
      clone.style.zIndex = '9999';
      document.body.appendChild(clone);

      // Wait for any images in the clone to load
      const cloneImages = clone.querySelectorAll('img');
      await Promise.all(
        Array.from(cloneImages).map(img => {
          return new Promise((resolve) => {
            if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
              resolve();
              return;
            }
            img.onload = () => {
              if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                // Replace with placeholder on zero dimensions
                const parent = img.parentNode;
                const placeholder = document.createElement('span');
                placeholder.textContent = '📸';
                placeholder.style.cssText = `
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  width: ${img.width || 100}px;
                  height: ${img.height || 100}px;
                  font-size: 40px;
                  background: #f0f0f0;
                  border-radius: 8px;
                `;
                parent.replaceChild(placeholder, img);
              }
              resolve();
            };
            img.onerror = () => {
              // Replace with placeholder on error
              const parent = img.parentNode;
              const placeholder = document.createElement('span');
              placeholder.textContent = '📸';
              placeholder.style.cssText = `
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: ${img.width || 100}px;
                height: ${img.height || 100}px;
                font-size: 40px;
                background: #f0f0f0;
                border-radius: 8px;
              `;
              parent.replaceChild(placeholder, img);
              resolve();
            };
            // If image already has valid dimensions
            if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
              resolve();
            }
          });
        })
      );

      // Wait a bit for rendering
      await new Promise(resolve => setTimeout(resolve, 500));

      // Render the clone
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: clone.scrollWidth,
        height: clone.scrollHeight,
        windowWidth: clone.scrollWidth,
        windowHeight: clone.scrollHeight,
        onclone: (document, element) => {
          const imgs = element.querySelectorAll('img');
          imgs.forEach(img => {
            img.crossOrigin = 'anonymous';
          });
        }
      });

      // Remove the clone
      document.body.removeChild(clone);

      // Download
      const link = document.createElement('a');
      link.download = `AICIS-ID-${formData.name.replace(/\s/g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert('✅ ID Card downloaded successfully!');

    } catch (error) {
      console.error('Error downloading ID card:', error);
      alert('❌ Unable to download. Please try again.\n\nError: ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleReset = () => {
    setStep('form');
    setFormData({
      name: '',
      nationality: '',
      institutionType: '',
      institutionName: '',
      position: '',
      phone: '',
      email: '',
      institutionContactPerson: '',
      institutionPhone: '',
      institutionEmail: '',
      invitationCode: '',
      photo: null,
      photoPreview: null
    });
    setCodeError('');
    setValidationErrors({});
    setIdData(null);
    setShowGuidelines(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const acceptGuidelines = () => {
    setShowGuidelines(false);
  };

  if (!isOpen) return null;

  return (
    <div className="signin-modal-overlay" onClick={handleClose}>
      <div className="signin-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>×</button>
        
        {step === 'form' && (
          <div className="signin-form-container">
            {showGuidelines && (
              <div className="guidelines-overlay">
                <div className="guidelines-content">
                  <div className="guidelines-header">
                    <span className="guidelines-badge">📋 IMPORTANT NOTICE</span>
                    <h2>Registration Guidelines</h2>
                  </div>
                  <div className="guidelines-body">
                    <div className="guideline-item">
                      <span className="guideline-icon">🔒</span>
                      <div>
                        <h4>Invitation Only</h4>
                        <p>This form is strictly for specially invited institutions only. Accreditation is required.</p>
                      </div>
                    </div>
                    <div className="guideline-item">
                      <span className="guideline-icon">📝</span>
                      <div>
                        <h4>Invitation Code Required</h4>
                        <p>You must enter the invitation code provided in your official invitation letter.</p>
                      </div>
                    </div>
                    <div className="guideline-item">
                      <span className="guideline-icon">📸</span>
                      <div>
                        <h4>Photo Upload</h4>
                        <p>Upload a clear passport-style photograph for your ID card.</p>
                      </div>
                    </div>
                    <div className="guideline-item">
                      <span className="guideline-icon">✅</span>
                      <div>
                        <h4>Verification</h4>
                        <p>All information provided will be verified against our invitation records.</p>
                      </div>
                    </div>
                  </div>
                  <div className="guidelines-footer">
                    <p className="guidelines-note">
                      <strong>Note:</strong> This registration is for <strong>accredited institutions</strong> only. 
                      The invitation code is required to complete registration.
                    </p>
                    <button onClick={acceptGuidelines} className="guidelines-accept-btn">
                      I Understand, Continue →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!showGuidelines && (
              <>
                <div className="signin-header">
                  <div className="signin-badge">PRE-SUMMIT REGISTRATION</div>
                  <h2>Diplomatic & Inter-Ministerial</h2>
                  <p>Stakeholders Engagement Workshop</p>
                  <div className="signin-line"></div>
                  <div className="signin-accreditation-badge">
                    🔒 Accredited Institution Only
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="signin-form">
                  {/* Invitation Code */}
                  <div className="form-group">
                    <label>Invitation Code *</label>
                    <input
                      type="text"
                      name="invitationCode"
                      placeholder="Enter the invitation code from your letter"
                      value={formData.invitationCode}
                      onChange={handleChange}
                      required
                      className={validationErrors.invitationCode || codeError ? 'input-error' : ''}
                    />
                    {(validationErrors.invitationCode || codeError) && (
                      <span className="error-message">{validationErrors.invitationCode || codeError}</span>
                    )}
                    <small className="form-hint">Enter the invitation code provided in your official invitation letter</small>
                  </div>

                  {/* Name */}
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={validationErrors.name ? 'input-error' : ''}
                    />
                    {validationErrors.name && <span className="error-message">{validationErrors.name}</span>}
                  </div>

                  {/* Nationality */}
                  <div className="form-group">
                    <label>Nationality *</label>
                    <select
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleChange}
                      required
                      className={validationErrors.nationality ? 'input-error' : ''}
                    >
                      <option value="">Select your nationality</option>
                      {nationalities.map((nat) => (
                        <option key={nat} value={nat}>{nat}</option>
                      ))}
                    </select>
                    {validationErrors.nationality && <span className="error-message">{validationErrors.nationality}</span>}
                  </div>

                  {/* Institution Type */}
                  <div className="form-group">
                    <label>Institution Type *</label>
                    <select
                      name="institutionType"
                      value={formData.institutionType}
                      onChange={handleChange}
                      required
                      className={validationErrors.institutionType ? 'input-error' : ''}
                    >
                      <option value="">Select institution type</option>
                      {institutionTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    {validationErrors.institutionType && <span className="error-message">{validationErrors.institutionType}</span>}
                  </div>

                  {/* Institution Name */}
                  <div className="form-group">
                    <label>
                      {formData.institutionType === 'Government' 
                        ? 'Agency / Ministry / Diplomatic Mission *' 
                        : 'Name of Institution *'}
                    </label>
                    <input
                      type="text"
                      name="institutionName"
                      placeholder={
                        formData.institutionType === 'Government' 
                          ? 'Enter agency, ministry or diplomatic mission' 
                          : 'Enter your institution name'
                      }
                      value={formData.institutionName}
                      onChange={handleChange}
                      required
                      className={validationErrors.institutionName ? 'input-error' : ''}
                    />
                    {validationErrors.institutionName && <span className="error-message">{validationErrors.institutionName}</span>}
                  </div>

                  {/* Position */}
                  <div className="form-group">
                    <label>Position / Title *</label>
                    <input
                      type="text"
                      name="position"
                      placeholder="Enter your position or title"
                      value={formData.position}
                      onChange={handleChange}
                      required
                      className={validationErrors.position ? 'input-error' : ''}
                    />
                    {validationErrors.position && <span className="error-message">{validationErrors.position}</span>}
                  </div>

                  {/* Phone Number */}
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+234 800 000 0000"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className={validationErrors.phone ? 'input-error' : ''}
                    />
                    {validationErrors.phone && <span className="error-message">{validationErrors.phone}</span>}
                    <small className="form-hint">Include country code (e.g., +234 for Nigeria)</small>
                  </div>

                  {/* Email Address */}
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={validationErrors.email ? 'input-error' : ''}
                    />
                    {validationErrors.email && <span className="error-message">{validationErrors.email}</span>}
                  </div>

                  {/* Institution Contact Person */}
                  <div className="form-group">
                    <label>Institution Contact Person *</label>
                    <input
                      type="text"
                      name="institutionContactPerson"
                      placeholder="Enter institution contact person's name"
                      value={formData.institutionContactPerson}
                      onChange={handleChange}
                      required
                      className={validationErrors.institutionContactPerson ? 'input-error' : ''}
                    />
                    {validationErrors.institutionContactPerson && <span className="error-message">{validationErrors.institutionContactPerson}</span>}
                  </div>

                  {/* Institution Phone Number */}
                  <div className="form-group">
                    <label>Institution Phone Number *</label>
                    <input
                      type="tel"
                      name="institutionPhone"
                      placeholder="+234 800 000 0000"
                      value={formData.institutionPhone}
                      onChange={handleChange}
                      required
                      className={validationErrors.institutionPhone ? 'input-error' : ''}
                    />
                    {validationErrors.institutionPhone && <span className="error-message">{validationErrors.institutionPhone}</span>}
                    <small className="form-hint">Include country code</small>
                  </div>

                  {/* Institution Email */}
                  <div className="form-group">
                    <label>Institution Email *</label>
                    <input
                      type="email"
                      name="institutionEmail"
                      placeholder="institution@example.com"
                      value={formData.institutionEmail}
                      onChange={handleChange}
                      required
                      className={validationErrors.institutionEmail ? 'input-error' : ''}
                    />
                    {validationErrors.institutionEmail && <span className="error-message">{validationErrors.institutionEmail}</span>}
                  </div>

                  {/* Photo Upload */}
                  <div className="form-group">
                    <label>Upload Clear Photo *</label>
                    <div className="photo-upload-wrapper">
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/jpeg,image/png,image/jpg"
                        onChange={handlePhotoChange}
                        required={!formData.photoPreview}
                        className={`photo-input ${validationErrors.photo ? 'input-error' : ''}`}
                      />
                      {formData.photoPreview && (
                        <div className="photo-preview">
                          <img src={formData.photoPreview} alt="Preview" />
                          <button 
                            type="button" 
                            className="photo-remove"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                photo: null,
                                photoPreview: null
                              });
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                          >
                            ×
                          </button>
                        </div>
                      )}
                      {validationErrors.photo && <span className="error-message">{validationErrors.photo}</span>}
                    </div>
                    <small className="form-hint">Upload a clear passport-style photo (JPEG or PNG, max 5MB)</small>
                  </div>

                  <button type="submit" className="signin-submit-btn">
                    Generate ID Card
                  </button>

                  <p className="form-footer-note">
                    🔒 By submitting, you confirm that you are an accredited representative of your institution.
                  </p>
                </form>
              </>
            )}
          </div>
        )}

        {step === 'idcard' && (
          <div className="id-card-container">
            <div className="id-card-header">
              <span className="id-card-badge">✓ REGISTRATION COMPLETE</span>
              <h2>Your ID Card</h2>
              <p>Download your ID card for event entry</p>
            </div>

            {/* ID Card */}
            <div 
              className="id-card-wrapper" 
              ref={idCardRef}
              style={{
                backgroundImage: 'url(/images/id-card-bg.jpeg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.2)',
                position: 'relative'
              }}
            >
              <div 
                className="id-card-overlay"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.80)',
                  zIndex: 1,
                  pointerEvents: 'none',
                  borderRadius: '16px'
                }}
              ></div>
              
              <div className="id-card" style={{ position: 'relative', zIndex: 2 }}>
                <div className="id-card-inner" style={{ position: 'relative', zIndex: 3, padding: '25px 30px 22px' }}>
                  
                  {/* TOP LOGOS */}
                  <div className="id-card-logos-top" style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    gap: '30px', 
                    padding: '8px 0 12px',
                    borderBottom: '2px solid rgba(43, 16, 7, 0.08)',
                    marginBottom: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <div className="id-card-logo-item">
                      <img 
                        src="/images/hero/presidency.png" 
                        alt="Presidency" 
                        style={{ height: '60px', width: 'auto', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div className="id-card-logo-item">
                      <img 
                        src="/images/hero/abvig-logo.jpg" 
                        alt="ABVIG" 
                        style={{ height: '75px', width: 'auto', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div className="id-card-logo-item">
                      <img 
                        src="/images/hero/foreign-affairs.jpg" 
                        alt="Foreign Affairs" 
                        style={{ height: '60px', width: 'auto', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div className="id-card-logo-item">
                      <img 
                        src="/images/hero/zenith-w-bg.jpeg" 
                        alt="Zenith" 
                        style={{ height: '60px', width: 'auto', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  </div>

                  {/* MAIN TITLE */}
                  <div className="id-card-main-title" style={{ textAlign: 'center', marginBottom: '4px' }}>
                    <span style={{ 
                      fontSize: '24px', 
                      fontWeight: 900, 
                      color: '#0B4F2D', 
                      letterSpacing: '2px',
                      textShadow: '0 1px 4px rgba(255,255,255,0.5)',
                      display: 'block'
                    }}>
                      AICIS <span style={{ color: '#b8860b', fontWeight: 900 }}>2026</span> GLOBAL SUMMIT
                    </span>
                  </div>

                  {/* SUBTITLE */}
                  <div className="id-card-subtitle" style={{ textAlign: 'center', marginBottom: '2px' }}>
                    <span style={{ 
                      fontSize: '15px', 
                      fontWeight: 800, 
                      color: '#2B1007', 
                      letterSpacing: '1.5px',
                      textShadow: '0 1px 4px rgba(255,255,255,0.5)',
                      display: 'block'
                    }}>
                      DIPLOMATIC AND INTER-MINISTERIAL
                    </span>
                  </div>

                  {/* WORKSHOP TITLE */}
                  <div className="id-card-workshop" style={{ textAlign: 'center', marginBottom: '10px' }}>
                    <span style={{ 
                      fontSize: '13px', 
                      fontWeight: 800, 
                      color: '#2B1007', 
                      letterSpacing: '1px',
                      textShadow: '0 1px 4px rgba(255,255,255,0.5)',
                      display: 'block'
                    }}>
                      STAKEHOLDERS ENGAGEMENT WORKSHOP
                    </span>
                  </div>

                  {/* PRE-SUMMIT BADGE */}
                  <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                    <span style={{
                      display: 'inline-block',
                      background: '#2B1007',
                      color: '#ffffff',
                      padding: '5px 24px',
                      borderRadius: '50px',
                      fontSize: '12px',
                      fontWeight: 900,
                      letterSpacing: '3px',
                      textTransform: 'uppercase',
                      boxShadow: '0 2px 10px rgba(43, 16, 7, 0.25)'
                    }}>
                      PRE-SUMMIT
                    </span>
                  </div>

                  <div className="id-card-divider" style={{ margin: '6px 0 12px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(43,16,7,0.1), transparent)' }}></div>

                  {/* PHOTO AND DETAILS */}
                  <div className="id-card-body" style={{ display: 'flex', gap: '25px', alignItems: 'flex-start' }}>
                    <div className="id-card-photo" style={{ 
                      width: '120px', 
                      height: '140px', 
                      borderRadius: '10px', 
                      border: '4px solid #2B1007', 
                      flexShrink: 0, 
                      background: 'rgba(255,255,255,0.3)', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      overflow: 'hidden' 
                    }}>
                      {formData.photoPreview ? (
                        <img src={formData.photoPreview} alt="Participant" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ fontSize: '48px', color: '#ccc' }}>📸</div>
                      )}
                    </div>
                    <div className="id-card-details" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div className="id-card-field" style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <span className="id-card-label" style={{ fontSize: '11px', fontWeight: 800, color: '#2B1007', textTransform: 'uppercase', letterSpacing: '1px', minWidth: '85px' }}>Name:</span>
                        <span className="id-card-value" style={{ fontSize: '18px', fontWeight: 900, color: '#0B4F2D', textShadow: '0 1px 4px rgba(255,255,255,0.5)' }}>{formData.name}</span>
                      </div>
                      <div className="id-card-field" style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <span className="id-card-label" style={{ fontSize: '11px', fontWeight: 800, color: '#2B1007', textTransform: 'uppercase', letterSpacing: '1px', minWidth: '85px' }}>Institution:</span>
                        <span className="id-card-value" style={{ fontSize: '16px', fontWeight: 800, color: '#0B4F2D', textShadow: '0 1px 4px rgba(255,255,255,0.5)' }}>{formData.institutionName}</span>
                      </div>
                      <div className="id-card-field" style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <span className="id-card-label" style={{ fontSize: '11px', fontWeight: 800, color: '#2B1007', textTransform: 'uppercase', letterSpacing: '1px', minWidth: '85px' }}>Position:</span>
                        <span className="id-card-value" style={{ fontSize: '16px', fontWeight: 800, color: '#0B4F2D', textShadow: '0 1px 4px rgba(255,255,255,0.5)' }}>{formData.position}</span>
                      </div>
                      <div className="id-card-field" style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <span className="id-card-label" style={{ fontSize: '11px', fontWeight: 800, color: '#2B1007', textTransform: 'uppercase', letterSpacing: '1px', minWidth: '85px' }}>Nationality:</span>
                        <span className="id-card-value" style={{ fontSize: '16px', fontWeight: 800, color: '#0B4F2D', textShadow: '0 1px 4px rgba(255,255,255,0.5)' }}>{formData.nationality}</span>
                      </div>
                      <div className="id-card-field" style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                        <span className="id-card-label" style={{ fontSize: '11px', fontWeight: 800, color: '#2B1007', textTransform: 'uppercase', letterSpacing: '1px', minWidth: '85px' }}>Contact:</span>
                        <span className="id-card-value" style={{ fontSize: '15px', fontWeight: 700, color: '#0B4F2D', textShadow: '0 1px 4px rgba(255,255,255,0.5)' }}>{formData.institutionContactPerson}</span>
                      </div>
                    </div>
                  </div>

                  <div className="id-card-divider" style={{ margin: '10px 0 12px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(43,16,7,0.1), transparent)' }}></div>

                  {/* FOOTER */}
                  <div className="id-card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div className="id-card-id" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className="id-label" style={{ fontSize: '11px', fontWeight: 800, color: '#2B1007', textTransform: 'uppercase', letterSpacing: '1px' }}>ID:</span>
                      <span className="id-number" style={{ fontSize: '18px', fontWeight: 900, color: '#2B1007', letterSpacing: '1.5px', textShadow: '0 1px 4px rgba(255,255,255,0.5)' }}>{idData?.idNumber || 'AICIS-2026-00001'}</span>
                    </div>
                    <div className="id-card-date">
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#2B1007' }}>Issued: {idData?.date || new Date().toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* WEBSITE LINK */}
                  <div className="id-card-website" style={{ textAlign: 'center', marginTop: '8px', paddingTop: '6px' }}>
                    <a 
                      href="https://aicis2026.abvig.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        fontSize: '13px', 
                        fontWeight: 900, 
                        color: '#1a73e8',
                        textShadow: '0 1px 4px rgba(255,255,255,0.5)',
                        letterSpacing: '0.5px',
                        textDecoration: 'none'
                      }}
                    >
                      aicis2026.abvig.com
                    </a>
                  </div>

                  <div className="id-card-bottom" style={{ textAlign: 'center', marginTop: '6px', paddingTop: '8px', borderTop: '1px solid rgba(43, 16, 7, 0.08)', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span className="id-card-venue" style={{ fontSize: '13px', fontWeight: 800, color: '#2B1007', textShadow: '0 1px 4px rgba(255,255,255,0.5)' }}>Rotunda Hall, Ministry of Foreign Affairs, Abuja</span>
                    <span className="id-card-event" style={{ fontSize: '12px', fontWeight: 700, color: '#2B1007', textShadow: '0 1px 4px rgba(255,255,255,0.5)' }}>Tuesday, 4th August 2026 | 10:00 AM</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="id-card-actions">
              <button 
                onClick={handleDownload} 
                className="id-card-download-btn"
                disabled={isDownloading}
              >
                {isDownloading ? '⏳ Generating...' : '⬇️ Download ID Card'}
              </button>
              <button onClick={handleReset} className="id-card-new-btn">
                Register New
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignInModal;